import { Injectable, inject } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { waitForFlutterWebView } from '../../utils/flutter.utils';
import { BrowserDetectService } from '../browser-detector/browser-detector.service';
import { ConfigService } from '../config-service/config-service.service';
import { WebFrontendConfig } from '../config-service/config.type';

/**
 * How a hand-off's off-origin leg is actually performed in *this* environment.
 *
 * This is the host-side seam that keeps the fx-widget contract portable. A
 * widget calls `sdk.handoff.start(kind, params)` and learns nothing else: not
 * whether it will be torn down, not whether a native shell is involved, not how
 * control was handed over. Everything environment-specific lives behind this
 * interface, and nothing behind it is ever projected onto a AppHandoffRecord
 * (see toWidgetHandoffRecord) or onto any protocol message.
 *
 * Two implementations ship today — the plain browser and the Flutter native
 * shell. A future non-web renderer (React Native was named) implements its own
 * transport behind this same interface and changes nothing else: not the
 * protocol, not the contract types, not the widget bundles. If a host can run a
 * hand-off in a native modal and come back to a *live* widget, that is legal —
 * the outcome reaches the widget through the 'handoffs' data topic push rather
 * than through a remount, and both paths are first-class by design.
 */
export interface WidgetHandoffTransport {
  readonly kind: WidgetHandoffTransportKind;

  /**
   * Outbound leg: hand control to `url` (a bank / vendor URL, never an in-app
   * route). Resolving means "the hand-off has been begun", never "the page
   * survived" or "the page did not survive".
   */
  startHandoff(url: string): Promise<void>;

  /**
   * Return leg, before the outcome is resolved.
   *
   * Resolves `true` when the transport has taken the return over and the caller
   * must do nothing further in this document (the iOS deep-link bounce below is
   * the only such case today). `false` means "carry on resolving here".
   */
  beginReturn(returnPath: string): Promise<boolean>;

  /**
   * Return leg, after the outcome has been written onto the record. This is
   * where a shell is told the external flow is over so it can drop whatever UI
   * it put up for it.
   */
  finishReturn(): Promise<void>;
}

export type WidgetHandoffTransportKind = 'browser' | 'flutter-shell';

/** The subset of the Flutter InAppWebView JS bridge hand-offs use. */
interface FlutterShellBridge {
  callHandler(name: string, args: unknown[]): unknown;
}

/**
 * How long to wait for the shell bridge's `callHandler` to become callable.
 * Mirrors the native probe in pisp-deposit.utils.ts:114.
 */
export const WIDGET_HANDOFF_SHELL_PROBE_TIMEOUT_MS = 1000;

function shellGlobal(): FlutterShellBridge | undefined {
  return (window as unknown as { flutter_inappwebview?: FlutterShellBridge }).flutter_inappwebview;
}

/**
 * Runtime detection, and the *only* place it happens. A shell is present when
 * it has injected its JS bridge onto the window.
 */
export function isNativeShellPresent(): boolean {
  return !!shellGlobal();
}

/**
 * Waits for the bridge to be usable, then falls back to a late-injected global.
 * The two-step is native parity: pisp-deposit.utils.ts:114-120 probes with
 * waitForFlutterWebView and logs when it never appears, and
 * banks-listing-widget.component.ts:141-142 re-reads the raw global when the
 * probe missed it.
 */
async function awaitShellBridge(timeoutMs: number): Promise<FlutterShellBridge | null> {
  const ready = (await waitForFlutterWebView(timeoutMs)) as FlutterShellBridge | null;
  if (ready) return ready;
  const late = shellGlobal();
  if (late && typeof late.callHandler === 'function') return late;
  // Only noisy when a shell is genuinely there but never became callable; an
  // absent global is the ordinary browser case, not an error.
  if (late) console.error('Flutter web view bridge present but not callable for a widget hand-off');
  return null;
}

/**
 * Plain browser: the hand-off is a full-page redirect off the origin and the
 * return is a real page load on the widget-handoff-return route. The widget's
 * document does not survive, which is exactly why the outcome is correlated
 * through the hand-off record rather than through a promise.
 */
export class BrowserWidgetHandoffTransport implements WidgetHandoffTransport {
  public readonly kind = 'browser' as const;

  constructor(private readonly navigate: (url: string) => void) {}

  public async startHandoff(url: string): Promise<void> {
    this.navigate(url);
  }

  public async beginReturn(): Promise<boolean> {
    return false;
  }

  public async finishReturn(): Promise<void> {
    // nothing to tell a browser
  }
}

/**
 * The web frontend running inside the Flutter native shell.
 *
 * Mirrors the shell handling the native PISP/AISP flows already do, and nothing
 * more:
 *
 * - Outbound: tell the shell an external flow is starting, then navigate — the
 *   same order as banks-listing-widget.component.ts:144-146
 *   (`callHandler('InitiateExternalUrl', [])` then `window.location.href = link`)
 *   and pisp-deposit.utils.ts:228. The shell is signalled with an empty
 *   argument list in every native call site; it is a "I am leaving, don't treat
 *   this as an in-app navigation" signal, not a URL handoff, so the URL still
 *   travels by ordinary navigation.
 * - Return: the shell does a *real* navigation back to the redirectUri, so the
 *   return leg is the same single page-load path the browser uses. The only
 *   shell-specific step is telling the shell the flow is over —
 *   `callHandler('redirectionComplete', [])`, as in
 *   gocardless-pisp-deposit-status.component.ts:71-81,
 *   nominated-account-status.component.ts:36-44 and pisp-deposit.utils.ts:255.
 * - iOS outside Safari/WebKit: the vendor cannot redirect back into the app, so
 *   the return is bounced through the app's deep link first — mirrors
 *   gocardless-pisp-deposit-status.component.ts:41-46 and its
 *   getAppDeepLinkUrl fallback at :62-69.
 *
 * Deliberately NOT mirrored: the custom-payment-page variant at
 * pisp-deposit.utils.ts:163-169, which opens the authorisation URL with
 * `window.open(url, '_blank')` and polls. A hand-off's outcome is correlated
 * through sessionStorage on the return route, and a `_blank` document only gets
 * a *copy* of sessionStorage — the completed record would never reach the
 * document the widget lives in. One navigation, one return path.
 */
export class FlutterShellWidgetHandoffTransport implements WidgetHandoffTransport {
  public readonly kind = 'flutter-shell' as const;

  constructor(
    private readonly options: {
      navigate: (url: string) => void;
      browserDetect: BrowserDetectService;
      loadConfig: () => Promise<WebFrontendConfig>;
      probeTimeoutMs?: number;
      awaitShell?: (timeoutMs: number) => Promise<FlutterShellBridge | null>;
    },
  ) {}

  public async startHandoff(url: string): Promise<void> {
    const shell = await this.shell();
    shell?.callHandler('InitiateExternalUrl', []);
    this.options.navigate(url);
  }

  public async beginReturn(returnPath: string): Promise<boolean> {
    if (!this.isIOSOutsideSafari()) return false;
    const config = await this.options.loadConfig();
    // Deep links end in '://', so the path must not also carry a leading slash.
    this.options.navigate(this.appDeepLinkUrl(config) + returnPath.replace(/^\/+/, ''));
    return true;
  }

  public async finishReturn(): Promise<void> {
    const shell = await this.shell();
    shell?.callHandler('redirectionComplete', []);
  }

  /**
   * Uses BrowserDetectService for both halves, matching the native return leg
   * (gocardless-pisp-deposit-status.component.ts:41-45). Note this is the
   * UAParser-backed check rather than the coarser `isIOS` regex in
   * device-detection.utils, which misses iPadOS 13+ (it reports as macOS).
   */
  private isIOSOutsideSafari(): boolean {
    return this.options.browserDetect.isIOS() && !this.options.browserDetect.isSafariBrowserOrWebkit();
  }

  private appDeepLinkUrl(config: WebFrontendConfig): string {
    if (config.appDeepLinkUrl) return config.appDeepLinkUrl;
    // Same fallback pair the native status page uses when config omits it.
    return window.location.origin.includes('development')
      ? 'app-thisbankdevelopment://'
      : 'app-thisbankprojects://';
  }

  private shell(): Promise<FlutterShellBridge | null> {
    const timeout = this.options.probeTimeoutMs ?? WIDGET_HANDOFF_SHELL_PROBE_TIMEOUT_MS;
    return (this.options.awaitShell ?? awaitShellBridge)(timeout);
  }
}

export interface WidgetHandoffTransportSelection {
  configService: ConfigService;
  browserDetect?: BrowserDetectService;
  /** Overrides how control actually leaves this document; tests substitute it. */
  navigate?: (url: string) => void;
  probeTimeoutMs?: number;
}

/**
 * The single place a transport is chosen. Synchronous on purpose: the outbound
 * leg runs from a user gesture long after boot, so the injected global is
 * conclusive — the same assumption banks-listing-widget.component.ts:141-142
 * makes when it reads `window.flutter_inappwebview` directly on click. The
 * *readiness* wait that pisp-deposit.utils.ts:114 does lives inside the shell
 * transport's methods instead, so it is paid only where a shell exists.
 *
 * An iOS browser that is not Safari/WebKit also selects the shell transport
 * even with no bridge on the window: that is precisely the case the native deep
 * link bounce exists for — the vendor sent the customer back into the wrong
 * browser, *outside* the app, so the bridge cannot be there and bouncing into
 * the app is the only way back. Every shell call is optional-chained, exactly
 * as the native call sites are, so a bridgeless shell transport degrades to a
 * plain navigation.
 */
export function selectWidgetHandoffTransport(
  selection: WidgetHandoffTransportSelection,
): WidgetHandoffTransport {
  const navigate = selection.navigate ?? ((url: string) => window.location.assign(url));
  // No ctor dependencies, so this is constructible outside an injection context
  // (the widget bridge is built from plain deps, not an injector).
  const browserDetect = selection.browserDetect ?? new BrowserDetectService();
  const shellOwnsThisApp =
    isNativeShellPresent() || (browserDetect.isIOS() && !browserDetect.isSafariBrowserOrWebkit());
  if (!shellOwnsThisApp) return new BrowserWidgetHandoffTransport(navigate);
  return new FlutterShellWidgetHandoffTransport({
    navigate,
    browserDetect,
    loadConfig: () => firstValueFrom(selection.configService.getConfig().pipe(take(1))),
    probeTimeoutMs: selection.probeTimeoutMs,
  });
}

/** DI wrapper for call sites that have an injector (the return route). */
@Injectable({ providedIn: 'root' })
export class WidgetHandoffTransportResolver {
  private readonly configService = inject(ConfigService);
  private readonly browserDetect = inject(BrowserDetectService);

  public resolve(): WidgetHandoffTransport {
    return selectWidgetHandoffTransport({
      configService: this.configService,
      browserDetect: this.browserDetect,
    });
  }
}
